import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useGetFieldAdminSiteDetailQuery, useSaveFieldAdminSiteNotesMutation } from "./fieldAdminApi";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { CARD_BG, BORDER, AMBER } from "../../Global/colors";
import { FONT_FAMILY, MONO_FONT_FAMILY, OK_GREEN_TEXT, relativeTime } from "./designTokens";
import { HSPageShell } from "./HS/HSStepShell";
import { STATUS_META } from "./Sites";
import { FAPrimaryButton } from "./FAControls";
import DesignSelect from "./DesignSelect";

export default function SiteDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: site, isSuccess, isError, error } = useGetFieldAdminSiteDetailQuery({ code });
  const [saveNotes, { isLoading: isSaving }] = useSaveFieldAdminSiteNotesMutation();
  const [notes, setNotes] = React.useState(null);

  React.useEffect(() => {
    if (!isSuccess || notes !== null) return;
    setNotes(site.notes || "");
  }, [isSuccess, site, notes]);

  if (isError) {
    const notFound = error?.status === 404;
    return (
      <HSPageShell>
        <Box sx={{ textAlign: "center", mt: "60px" }}>
          <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "1rem", fontWeight: 600, mb: "8px" }}>
            {notFound ? "Site not found" : "Couldn't load this site"}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.86rem", mb: "18px" }}>
            {notFound ? `No site matches "${code}".` : "Something went wrong fetching this site."}
          </Typography>
          <Typography
            onClick={() => navigate("/field-admin/sites/")}
            sx={{ color: AMBER, fontSize: "0.86rem", fontWeight: 600, cursor: "pointer" }}
          >
            ← Back to Sites
          </Typography>
        </Box>
      </HSPageShell>
    );
  }

  if (!isSuccess || notes === null) return null;

  const statusMeta = STATUS_META[site.status] || { label: site.status, bg: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.5)" };
  const forecast = site.forecast_installs;
  const actual = site.actual_installs;
  const pct = forecast === 0 ? 0 : Math.min(100, Math.round((actual / forecast) * 100));
  const dirty = notes !== (site.notes || "");

  // The design splits the address into a street line and a "suburb · zone" sub-line.
  const [street, ...rest] = site.address.split(",");
  const addressSub = [...rest.map((part) => part.trim()), site.zone].filter(Boolean).join(" · ");

  const handleSave = async () => {
    const result = await saveNotes({ code: site.code, notes });
    if (!result.error) dispatch(showSuccess("Site notes saved"));
  };

  return (
    <HSPageShell>
      {/* Site header */}
      <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "14px", px: "20px", py: "18px", mb: "18px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: MONO_FONT_FAMILY, fontSize: "0.86rem", fontWeight: 700, color: AMBER, letterSpacing: "0.04em" }}>
              {site.code}
            </Typography>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#fff", mt: "4px" }}>{street}</Typography>
            {addressSub ? (
              <Typography sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)", mt: "2px" }}>{addressSub}</Typography>
            ) : null}
          </Box>
          {/* .last-updated sits under the pill on the right, not beneath the address —
              the left column is the site's identity, the right its current state. */}
          <Box sx={{ textAlign: "right", flexShrink: 0 }}>
            <StatusPill meta={statusMeta} />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px", mt: "6px" }}>
              <AccessTimeIcon sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }} />
              <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)" }}>
                {relativeTime(site.updated_at)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* .map-placeholder — inside the hero card, under the address, rather than a section
            of its own at the foot of the page: it belongs to the site's identity, which is
            where the site is. Site has no coordinates (NEU-548), so the dashed frame states
            what would render instead of pretending to a map. */}
        <Box
          sx={{
            mt: "14px", height: { xs: "160px", sm: "200px" }, borderRadius: "10px",
            border: "1.5px dashed rgba(255,255,255,0.15)",
            background: "repeating-linear-gradient(45deg, rgba(255,255,255,0.012) 0 14px, rgba(255,255,255,0.025) 14px 28px)",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            gap: "8px", textAlign: "center", px: "16px",
          }}
        >
          <PlaceOutlinedIcon sx={{ color: "rgba(255,255,255,0.3)", fontSize: "2rem" }} />
          <Typography sx={{ fontSize: "0.86rem", fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>Map preview</Typography>
          <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.45)", lineHeight: 1.45, maxWidth: "380px" }}>
            Boundary and pin render once the site's coordinates are captured.
          </Typography>
        </Box>
      </Box>

      <SectionCard
        title="Status"
        sub="Current state &amp; reason. Approval flow stays with PM."
        right={<StatusPill meta={statusMeta} />}
      >
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0,1fr) 220px" }, gap: "16px", alignItems: "flex-start" }}>
          <Box>
            <FieldLabel>Reason / notes from approver</FieldLabel>
            {/* Read-only: this is the approver's text, not the FA's. Their own notes have
                their own section below, which is the field they can actually write. */}
            <Box sx={{
              backgroundColor: "#0D1117", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "8px",
              p: "10px 12px", minHeight: "76px", fontSize: "0.85rem", lineHeight: 1.5,
              color: site.decline_reason ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.35)",
            }}>
              {site.decline_reason || "No reason captured."}
            </Box>
          </Box>

          <Box>
            <FieldLabel>Change status</FieldLabel>
            <DesignSelect
              id="site-status"
              value={site.status}
              onChange={() => {}}
              disabled
              options={Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label }))}
            />
            <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", mt: "8px", lineHeight: 1.4 }}>
              Set by the PM. Status changes need their acknowledgement, which this app has no
              channel for yet, so the field is read-only here.
            </Typography>
          </Box>
        </Box>
      </SectionCard>

      {/* Notes — its own section in the design, and the only field on this page the field
          admin can actually write. It was folded into Status, which read as though the
          status and the note were saved together. */}
      {/* Installs — the design keeps the bars inside this card rather than a second one. */}
      <SectionCard title="Installs" sub="Forecasted vs actual completion · COC-issued by QE." right={`${pct}% complete`}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", mb: "16px" }}>
          <InstallTile label="Forecasted" value={forecast} tone="fcst" sub="Set by the PM" />
          <InstallTile
            label="Actual · COC-issued"
            value={actual}
            tone="actual"
            // The design reads "+2 since yesterday". Nothing records a daily delta per site,
            // so this states the gap that remains, which is the number the FA acts on —
            // except where the site is declined, since nothing more will be installed there.
            sub={site.status === "declined"
              ? "No further installs"
              : forecast > actual
              ? `${forecast - actual} still to install`
              : forecast ? "Forecast met" : null}
          />
        </Box>
        <BarRow label="Forecast" value={forecast} widthPct={100} fill="rgba(253,185,64,0.45)" />
        <Box sx={{ height: "10px" }} />
        <BarRow label="Actual" value={actual} widthPct={pct} fill="linear-gradient(90deg,#22C55E,#16A34A)" />
      </SectionCard>

      {/* .status-row — reason on the left, the status control on the right, per the
          design. The control is present and disabled rather than omitted: the write is
          refused on purpose (SiteDetailView.post documents why — the design's own two
          safeguards, PM approval and an acknowledgement logged to the activity timeline,
          do not exist), and a screen that simply leaves the control out cannot say so. */}
      <SectionCard title="Notes" sub="Free-text notes alongside the structured fields.">
        <Box
          component="textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Context for the PM or QE — access arrangements, resident contact, anything the next visit needs."
          sx={{
            width: "100%", boxSizing: "border-box", backgroundColor: "#0D1117",
            border: "1px solid rgba(255,255,255,0.08)", borderRadius: "8px", p: "12px 14px",
            color: "#fff", fontFamily: FONT_FAMILY, fontSize: "0.88rem", lineHeight: 1.5,
            resize: "vertical", minHeight: "100px", outline: "none",
            "&:focus": { borderColor: "rgba(253,185,64,0.5)" },
            "&::placeholder": { color: "rgba(255,255,255,0.3)" },
          }}
        />
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.4)", mt: "6px" }}>
          Visible to the QE and PM · part of the site's audit trail.
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", mt: "14px" }}>
          <Box
            component="button"
            type="button"
            disabled={!dirty || isSaving}
            onClick={() => setNotes(site.notes || "")}
            sx={{
              px: "14px", py: "9px", borderRadius: "9px", fontFamily: FONT_FAMILY, fontSize: "0.84rem", fontWeight: 600,
              backgroundColor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.8)", cursor: !dirty || isSaving ? "default" : "pointer",
              opacity: !dirty || isSaving ? 0.5 : 1,
            }}
          >
            Discard
          </Box>
          <FAPrimaryButton onClick={handleSave} disabled={!dirty || isSaving} sx={{ py: "9px", fontSize: "0.84rem" }}>
            {isSaving ? "Saving…" : "Save notes"}
          </FAPrimaryButton>
        </Box>
      </SectionCard>

    </HSPageShell>
  );
}

// The design wraps each group in a card with a title and sub-line, rather than the
// uppercase micro-label the H&S steps use.
function SectionCard({ title, sub, right, children }) {
  return (
    <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "14px", px: "20px", py: "18px", mb: "14px" }}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "10px", mb: "14px", flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>{title}</Typography>
          {sub ? <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)", mt: "2px" }}>{sub}</Typography> : null}
        </Box>
        {/* A string right-slot is the "67% complete" caption; a node is passed through as
            given, so the status pill is not wrapped in a <p> it would be invalid inside. */}
        {typeof right === "string"
          ? <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: AMBER, flexShrink: 0 }}>{right}</Typography>
          : right || null}
      </Box>
      {children}
    </Box>
  );
}

// .install-tile — the value takes .fcst (amber) or .actual (green). Both rendered white,
// which flattened the one contrast the section exists to draw: what was planned against
// what is actually certified. The bars below already used those two tones, so the tiles and
// the bars disagreed about which number was which.
// .reason-label
function FieldLabel({ children }) {
  return (
    <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.06em", mb: "6px" }}>
      {children}
    </Typography>
  );
}

function InstallTile({ label, value, tone, sub }) {
  return (
    <Box sx={{ backgroundColor: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "10px", px: "16px", py: "14px" }}>
      <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", letterSpacing: "0.08em", mb: "4px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "1.7rem", fontWeight: 700, color: tone === "actual" ? OK_GREEN_TEXT : AMBER, lineHeight: 1.05, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      {sub ? (
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", mt: "3px" }}>{sub}</Typography>
      ) : null}
    </Box>
  );
}

// 6px radius with a status glyph, not the 999px pill dev had.
function StatusPill({ meta }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "0.74rem", fontWeight: 700, px: "12px", py: "6px", borderRadius: "6px", letterSpacing: "0.03em", flexShrink: 0, backgroundColor: meta.bg, color: meta.color }}>
      <CheckIcon sx={{ fontSize: "0.85rem" }} />
      {meta.label}
    </Box>
  );
}

function BarRow({ label, value, widthPct, fill }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "80px 1fr 40px", alignItems: "center", gap: "12px" }}>
      <Typography sx={{ fontSize: "0.74rem", fontWeight: 600, color: "rgba(255,255,255,0.7)" }}>{label}</Typography>
      <Box sx={{ height: "14px", backgroundColor: "rgba(255,255,255,0.05)", borderRadius: "4px", overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${widthPct}%`, background: fill, borderRadius: "4px" }} />
      </Box>
      <Typography sx={{ fontSize: "0.76rem", color: "#fff", fontWeight: 700, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    </Box>
  );
}
